/* eslint-disable no-undef */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CloudRounded,
  DnsRounded,
  ExpandLess,
  ExpandMore,
  GitHub,
  RefreshRounded
} from '@mui/icons-material';
import { SvgIcon } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getLeadConnectionsFromUser,
  setPostIntegrationReturnPath
} from '../../utils/integrations';
import { refreshUserInfoRequest } from '../../actions';
import { sendRequest } from '../../utils/dateHelpers';

const IntegrationCard = ({
  title,
  description,
  icon: Icon,
  buttonText,
  guideLink,
  color,
  supportedProviders,
  isDisabled,
  onConnect,
  onGuide
}) => {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-stroke/80 bg-white/95 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl dark:border-strokedark dark:bg-boxdark/95">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-28 opacity-80"
        style={{
          background: `linear-gradient(180deg, ${color}1f 0%, transparent 100%)`
        }}
      />
      <div className="relative flex h-full flex-col p-6 lg:p-8">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-xl"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon sx={{ fontSize: 30, color: color }} />
          </div>
          {supportedProviders?.length > 0 && (
            <div className="flex items-center -space-x-1.5">
              {supportedProviders.map(
                ({ icon: ProviderIcon, label, color: providerColor }) => (
                  <span
                    key={label}
                    title={label}
                    aria-label={label}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-slate-50 shadow-sm dark:border-boxdark dark:bg-slate-800"
                  >
                    <ProviderIcon sx={{ fontSize: 17, color: providerColor }} />
                  </span>
                )
              )}
            </div>
          )}
        </div>

        <h3 className="mb-2 text-xl font-bold tracking-tight text-black dark:text-white">
          {title}
        </h3>
        <p className="mb-6 text-sm leading-6 text-body-color dark:text-body-color-dark">
          {description}
        </p>

        <div className="mt-auto">
          {guideLink && (
            <button
              type="button"
              onClick={onGuide}
              className="mb-3 inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-center text-sm font-semibold text-slate-700 transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-700/80"
            >
              View Setup Guide
            </button>
          )}
          {isDisabled ? (
            <button
              type="button"
              disabled
              className="inline-flex w-full cursor-not-allowed items-center justify-center rounded-xl px-6 py-3 text-center text-sm font-semibold text-white opacity-60"
              style={{ backgroundColor: color }}
            >
              Unavailable
            </button>
          ) : (
            <button
              type="button"
              onClick={onConnect}
              className="inline-flex w-full items-center justify-center rounded-xl px-6 py-3 text-center text-sm font-semibold text-white transition-all duration-300 hover:brightness-110"
              style={{ backgroundColor: color }}
            >
              {buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

IntegrationCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  guideLink: PropTypes.string,
  color: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  supportedProviders: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.elementType.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired
    })
  ),
  isDisabled: PropTypes.bool,
  onConnect: PropTypes.func.isRequired,
  onGuide: PropTypes.func
};

const CONSENT_INTRO = {
  company: 'Gitme OÜ',
  registration: 'Estonia Register Code: 17383519',
  description:
    'To use Gitme, you must review and accept the following terms regarding repository access, data security and confidentiality. By connecting your Git platform account or repository to Gitme, you confirm that you understand and accept the following:'
};

const CONSENT_SECTIONS = [
  {
    title: '1. Limited Access Model',
    body: 'Gitme operates under a limited-access and least-privilege model. Gitme only requests the minimum permissions required to provide developer analytics, engineering insights and related service functionality.'
  },
  {
    title: '2. No Password or Secret Collection',
    body: 'Gitme does not request, collect or permanently store your passwords, private keys, SSH keys, API secrets or similar sensitive credentials as part of its standard service model.'
  },
  {
    title: '3. No Permanent Codebase Copying',
    body: 'Gitme does not permanently copy, ingest or retain full repository contents; any technically required access is transient and solely for service delivery.'
  },
  {
    title: '4. No Full Repository Exposure to LLMs',
    body: 'Gitme does not provide any LLM or similar AI system with unrestricted access to your full repository, complete codebase or full software product source code. Where AI-assisted processing is used, it is limited, task-specific and proportionate, and does not involve bulk exposure of the full codebase.'
  },
  {
    title: '5. Processing Scope',
    body: 'Gitme may process limited repository metadata, commit history, pull request context, issue-related information, change-level data and other technically necessary information solely for the purpose of delivering analytics, benchmarking, reporting, product functionality, security, support and service improvement.'
  },
  {
    title: '6. Confidentiality',
    body: 'Gitme treats non-public repository information, technical data and service outputs as confidential and limits internal access to authorized personnel on a need-to-know basis.'
  },
  {
    title: '7. Ownership',
    body: 'Your source code, repositories, technical documentation, trade secrets and related intellectual property remain fully owned by you or your licensors. Your use of Gitme does not transfer ownership of your code or related assets to Gitme.'
  },
  {
    title: '8. Access Control and Revocation',
    body: 'You control which repositories, projects, organizations or scopes are connected to Gitme. You may revoke Gitme’s access at any time through the relevant Git platform settings, subject to any temporary retention required for security, compliance or operational purposes.'
  },
  {
    title: '9. Data Retention and Deletion',
    body: 'Gitme retains data only for as long as reasonably necessary to provide the service, maintain security, comply with legal obligations and support legitimate operational needs. Upon account termination or valid request, Gitme will delete, anonymize or render inaccessible retained data, unless retention is required by law or necessary for security logging and recordkeeping.'
  },
  {
    title: '10. Authority to Connect',
    body: 'By proceeding, you represent and warrant that you are authorized to connect the relevant repositories, projects, organizations or development data to Gitme and to accept these terms on behalf of yourself and, where applicable, your organization.'
  },
  {
    title: '11. Governing Law',
    body: 'This consent and the related use of Gitme shall be governed by the laws of Estonia, unless otherwise agreed in a separate written contract between Gitme and your organization.'
  }
];

const CONNECTING_STAGES = [
  { progress: 18, label: 'Verifying access' },
  { progress: 44, label: 'Securing transit' },
  { progress: 72, label: 'Preparing redirect' },
  { progress: 100, label: 'Ready to connect' }
];

const GitLabIcon = props => (
  <SvgIcon {...props}>
    <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .41.26l2.75 8.43h8.04l2.75-8.43a.43.43 0 0 1 .41-.26.42.42 0 0 1 .39.29l3.66 11.29a.84.84 0 0 1-.3.94z" />
  </SvgIcon>
);

const BitbucketIcon = props => (
  <SvgIcon {...props}>
    <path d="M2.648 3.223A1.564 1.564 0 0 1 4.197 2h15.606a1.564 1.564 0 0 1 1.549 1.223l2.613 15.672a1.564 1.564 0 0 1-1.549 1.905H1.584a1.564 1.564 0 0 1-1.549-1.905L2.648 3.223zM14.28 12.01l-1.636-4.328h-1.288l-1.635 4.328h-2.31l1.76-6.195 1.58-2.618h2.496l1.58 2.618 1.76 6.195h-2.308z" />
  </SvgIcon>
);

const CONNECTED_INTEGRATION_META = {
  github: {
    icon: GitHub,
    color: '#24292e',
    mode: 'Cloud'
  },
  gitlab: {
    icon: GitLabIcon,
    color: '#FC6D26',
    mode: 'Cloud'
  },
  bitbucket: {
    icon: BitbucketIcon,
    color: '#0052CC',
    mode: 'Cloud'
  },
  github_enterprise: {
    icon: GitHub,
    color: '#24292e',
    mode: 'Self Hosted'
  },
  gitlab_enterprise: {
    icon: GitLabIcon,
    color: '#FC6D26',
    mode: 'Self Hosted'
  },
  bitbucket_server: {
    icon: BitbucketIcon,
    color: '#0052CC',
    mode: 'Self Hosted'
  }
};

const getConnectedIntegrationMeta = type =>
  CONNECTED_INTEGRATION_META[type] || {
    icon: GitHub,
    color: '#475569',
    mode: 'Cloud'
  };

const getConnectionRepositories = connection =>
  Array.isArray(connection?.repositories) ? connection.repositories : [];

const getConnectionTotalRepoCount = connection => {
  const totalRepoCount = Number(connection?.totalRepoCount);

  if (Number.isFinite(totalRepoCount) && totalRepoCount >= 0) {
    return totalRepoCount;
  }

  return getConnectionRepositories(connection).length;
};

const getConnectionActiveRepoCount = connection => {
  const activeRepoCount = Number(connection?.activeRepoCount);

  if (Number.isFinite(activeRepoCount) && activeRepoCount >= 0) {
    return activeRepoCount;
  }

  return getConnectionRepositories(connection).filter(
    repository => repository?.status === 'active'
  ).length;
};

const getConnectionPassiveRepoCount = connection => {
  const passiveRepoCount = Number(connection?.passiveRepoCount);

  if (Number.isFinite(passiveRepoCount) && passiveRepoCount >= 0) {
    return passiveRepoCount;
  }

  const totalRepoCount = getConnectionTotalRepoCount(connection);
  const activeRepoCount = getConnectionActiveRepoCount(connection);
  return Math.max(totalRepoCount - activeRepoCount, 0);
};

const getRepositoryStatusClasses = repository =>
  repository?.status === 'active'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
    : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200';

const Integrations = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [integrationMode, setIntegrationMode] = useState('cloud');
  const [pendingIntegration, setPendingIntegration] = useState(null);
  const [expandedConnectionIds, setExpandedConnectionIds] = useState({});
  const [reauthorizingConnectionId, setReauthorizingConnectionId] =
    useState(null);
  const [hasAcceptedConsent, setHasAcceptedConsent] = useState(false);
  const [hasScrolledToConsentEnd, setHasScrolledToConsentEnd] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasVerifiedBillingProfile, setHasVerifiedBillingProfile] =
    useState(false);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [connectionProgress, setConnectionProgress] = useState(
    CONNECTING_STAGES[0].progress
  );
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTING_STAGES[0].label
  );
  const consentContentRef = useRef(null);
  const connectionTimersRef = useRef([]);
  const postIntegrationRefreshTimersRef = useRef([]);
  const { user } = useSelector(state => state);
  const integrationResult = useMemo(
    () => new URLSearchParams(location.search).get('integration'),
    [location.search]
  );
  const connectedLeadConnections = useMemo(
    () => getLeadConnectionsFromUser(user),
    [user]
  );

  useEffect(() => {
    if (connectedLeadConnections.length === 0) {
      setHasVerifiedBillingProfile(false);
      setHasPaidSubscription(false);
      return undefined;
    }

    let isActive = true;
    setHasVerifiedBillingProfile(false);

    sendRequest('/billing/profile', 'GET')
      .then(profile => {
        if (!isActive) {
          return;
        }

        setHasPaidSubscription(Boolean(profile?.subscription));
        setHasVerifiedBillingProfile(true);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setHasPaidSubscription(false);
        setHasVerifiedBillingProfile(false);
      });

    return () => {
      isActive = false;
    };
  }, [connectedLeadConnections.length]);

  const clearPostIntegrationRefreshTimers = () => {
    postIntegrationRefreshTimersRef.current.forEach(timerId =>
      window.clearTimeout(timerId)
    );
    postIntegrationRefreshTimersRef.current = [];
  };

  useEffect(() => {
    if (integrationResult === 'reauthorization-failed') {
      clearPostIntegrationRefreshTimers();
      toast.error(
        'Authorization could not be refreshed. Your existing connection was not changed.'
      );
      window.history.replaceState({}, document.title, location.pathname);
      return undefined;
    }

    if (
      integrationResult !== 'connected' &&
      integrationResult !== 'reauthorized'
    ) {
      clearPostIntegrationRefreshTimers();
      return undefined;
    }

    clearPostIntegrationRefreshTimers();

    if (integrationResult === 'reauthorized') {
      toast.success('Integration authorization refreshed successfully.');
    }

    [0, 1500, 4000].forEach(delay => {
      const timerId = window.setTimeout(() => {
        dispatch(refreshUserInfoRequest());
      }, delay);

      postIntegrationRefreshTimersRef.current.push(timerId);
    });

    window.history.replaceState({}, document.title, location.pathname);

    return () => {
      clearPostIntegrationRefreshTimers();
    };
  }, [dispatch, integrationResult, location.pathname]);

  const toggleConnectionExpansion = connectionId => {
    setExpandedConnectionIds(currentState => ({
      ...currentState,
      [connectionId]: !currentState[connectionId]
    }));
  };

  const handleRefreshAuthorization = async connection => {
    if (!connection?.id || reauthorizingConnectionId) {
      return;
    }

    setReauthorizingConnectionId(connection.id);
    try {
      const response = await sendRequest(
        `/integrations/${encodeURIComponent(connection.id)}/reauthorize`,
        'POST'
      );
      if (!response?.authorizeUrl) {
        throw new Error('Authorization URL was not returned.');
      }
      window.location.href = response.authorizeUrl;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Could not open the authorization screen.'
      );
      setReauthorizingConnectionId(null);
    }
  };

  const cloudIntegrations = [
    {
      title: 'GitHub',
      description:
        'Connect your GitHub account to import repositories and track metrics.',
      buttonText: 'Connect GitHub',
      link: process.env.REACT_APP_INTEGRATION_GITHUB_LINK,
      leadType: 'github',
      color: '#24292e', // GitHub black
      icon: GitHub
    },
    {
      title: 'GitLab',
      description:
        'Integrate existing GitLab workflows to streamline your development process.',
      buttonText: 'Connect GitLab',
      // Previous GitLab OAuth client_id: 09f2488aafd0e4c4d1e2da21a4c788341166a72faa0691ebee3cff4059477fa3
      link: process.env.REACT_APP_INTEGRATION_GITLAB_LINK,
      leadType: 'gitlab',
      color: '#FC6D26', // GitLab orange
      icon: GitLabIcon
    },
    {
      title: 'Bitbucket',
      description:
        'Connect Bitbucket repositories to visualize your team work.',
      buttonText: 'Connect Bitbucket',
      // Previous Bitbucket OAuth client_id: 5EL77f6XkNjJRWgW2S
      link: process.env.REACT_APP_INTEGRATION_BITBUCKET_LINK,
      leadType: 'bitbucket',
      color: '#0052CC', // Bitbucket blue
      icon: BitbucketIcon
    }
  ];

  const selfHostedIntegrations = useMemo(
    () => [
      {
        title: 'GitHub Enterprise',
        description:
          'Use your self-hosted GitHub Enterprise Server instance with Gitdotme.',
        buttonText: 'View Setup Guide',
        guideLink:
          'https://docs.gitdotme.com/docs/Integrations/github-enterprise-integration/',
        connectPath:
          '/settings/integrations/self-hosted/github-enterprise/connect',
        leadType: 'github_enterprise',
        color: '#24292e',
        icon: GitHub
      },
      {
        title: 'Self-Hosted GitLab',
        description:
          'Connect your on-prem GitLab deployment with a secure self-hosted flow.',
        buttonText: 'View Setup Guide',
        guideLink:
          'https://docs.gitdotme.com/docs/Integrations/self-hosted-gitlab-integration/',
        connectPath: '/settings/integrations/self-hosted/gitlab/connect',
        leadType: 'gitlab_enterprise',
        color: '#FC6D26',
        icon: GitLabIcon
      },
      {
        title: 'Self-Hosted Bitbucket',
        description:
          'Set up Bitbucket Server/Data Center integration for private infrastructure.',
        buttonText: 'View Setup Guide',
        guideLink:
          'https://docs.gitdotme.com/docs/Integrations/self-hosted-bitbucket-integration/',
        connectPath: '/settings/integrations/self-hosted/bitbucket/connect',
        leadType: 'bitbucket_server',
        color: '#0052CC',
        icon: BitbucketIcon
      }
    ],
    []
  );

  const displayedIntegrations =
    integrationMode === 'cloud' ? cloudIntegrations : selfHostedIntegrations;

  const clearConnectionTimers = () => {
    connectionTimersRef.current.forEach(timerId =>
      window.clearTimeout(timerId)
    );
    connectionTimersRef.current = [];
  };

  const resetConsentState = () => {
    setHasAcceptedConsent(false);
    setHasScrolledToConsentEnd(false);
    setIsConnecting(false);
    setConnectionProgress(CONNECTING_STAGES[0].progress);
    setConnectionStatus(CONNECTING_STAGES[0].label);
  };

  const handleStartConnection = integration => {
    clearConnectionTimers();
    setPendingIntegration(integration);
    setHasAcceptedConsent(false);
    setHasScrolledToConsentEnd(false);
    setIsConnecting(false);
    setConnectionProgress(CONNECTING_STAGES[0].progress);
    setConnectionStatus(CONNECTING_STAGES[0].label);
  };

  const handleStartSelfHostedConnection = integration => {
    handleStartConnection(integration);
  };

  const openSetupGuide = integration => {
    window.open(
      integration.guideLink || integration.link,
      '_blank',
      'noopener'
    );
  };

  const handlePrimaryConsentConfirm = () => {
    if (!hasAcceptedConsent) {
      return;
    }

    if (!pendingIntegration) {
      return;
    }

    if (pendingIntegration.connectPath) {
      window.location.href = pendingIntegration.connectPath;
      return;
    }

    setPostIntegrationReturnPath();
    clearConnectionTimers();
    setConnectionProgress(CONNECTING_STAGES[0].progress);
    setConnectionStatus(CONNECTING_STAGES[0].label);
    setIsConnecting(true);
  };

  const closeConsentFlow = () => {
    clearConnectionTimers();
    setPendingIntegration(null);
    resetConsentState();
  };

  const handleConsentScroll = event => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const hasReachedBottom = scrollHeight - scrollTop - clientHeight <= 8;

    if (hasReachedBottom) {
      setHasScrolledToConsentEnd(true);
    }
  };

  useEffect(() => {
    if (!pendingIntegration) {
      return;
    }

    const consentContent = consentContentRef.current;

    if (!consentContent) {
      return;
    }

    consentContent.scrollTop = 0;

    const hasScrollableOverflow =
      consentContent.scrollHeight > consentContent.clientHeight + 1;

    setHasScrolledToConsentEnd(!hasScrollableOverflow);
  }, [pendingIntegration]);

  useEffect(() => {
    if (!isConnecting || !pendingIntegration) {
      return undefined;
    }

    clearConnectionTimers();

    connectionTimersRef.current = [
      window.setTimeout(() => {
        setConnectionProgress(CONNECTING_STAGES[1].progress);
        setConnectionStatus(CONNECTING_STAGES[1].label);
      }, 1200),
      window.setTimeout(() => {
        setConnectionProgress(CONNECTING_STAGES[2].progress);
        setConnectionStatus(CONNECTING_STAGES[2].label);
      }, 3000),
      window.setTimeout(() => {
        setConnectionProgress(CONNECTING_STAGES[3].progress);
        setConnectionStatus(CONNECTING_STAGES[3].label);
      }, 5000),
      window.setTimeout(() => {
        window.location.href = pendingIntegration.link;
      }, 6000)
    ];

    return () => {
      clearConnectionTimers();
    };
  }, [isConnecting, pendingIntegration]);

  return (
    <div className="mx-auto flex max-w-270 flex-col">
      <div className="mb-6 rounded-lg border border-stroke/80 bg-white px-5 py-4 shadow-sm dark:border-strokedark dark:bg-boxdark">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <a
                href="https://docs.gitdotme.com/docs/Overview/security-and-privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700 transition-colors duration-200 hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
              >
                Zero Trust
              </a>
              <span className="text-sm font-semibold text-black dark:text-white">
                Connect your code host providers
              </span>
            </div>
            <p className="max-w-3xl text-sm leading-6 text-body-color dark:text-body-color-dark">
              Sync repositories, manage access, and keep engineering insights up
              to date.
            </p>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 xl:min-w-[520px]">
            <a
              href="https://panel.gitdotme.com/settings/invite-user"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-3.5 py-3 text-sm leading-5 text-indigo-900 transition-colors duration-200 hover:border-indigo-300 hover:bg-indigo-100/80 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-100"
            >
              <span className="block font-semibold">Need access?</span>
              Invite a teammate with the right permissions.
            </a>
            <div className="rounded-lg border border-sky-200 bg-sky-50/70 px-3.5 py-3 text-sm leading-5 text-sky-950 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100">
              <span className="block font-semibold">Manage sources</span>
              <a
                href="https://panel.gitdotme.com/settings/repositories"
                className="font-semibold underline decoration-sky-400 underline-offset-2"
              >
                Repositories
              </a>{' '}
              and{' '}
              <a
                href="https://panel.gitdotme.com/settings/project-management"
                className="font-semibold underline decoration-sky-400 underline-offset-2"
              >
                projects
              </a>
            </div>
          </div>
        </div>
      </div>

      {connectedLeadConnections.length > 0 && (
        <div className="order-last mt-8 rounded-2xl border border-stroke bg-white p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-black dark:text-white">
              Connected integrations
            </h3>
            <p className="mt-1 text-sm leading-6 text-body-color dark:text-body-color-dark">
              Expand a connection to see every linked repository and whether it
              is currently active or passive.
            </p>
          </div>

          <ul className="divide-y divide-stroke rounded-xl border border-stroke dark:divide-strokedark dark:border-strokedark">
            {connectedLeadConnections.map((connection, index) => {
              const meta = getConnectedIntegrationMeta(connection.type);
              const ProviderIcon = meta.icon;
              const connectionId =
                connection.id || `${connection.type}-${index}`;
              const repositories = getConnectionRepositories(connection);
              const totalRepoCount = getConnectionTotalRepoCount(connection);
              const activeRepoCount = getConnectionActiveRepoCount(connection);
              const passiveRepoCount =
                getConnectionPassiveRepoCount(connection);
              const isExpanded = !!expandedConnectionIds[connectionId];
              const isReauthorizing =
                reauthorizingConnectionId === connection.id;
              const detailParts = [
                Number.isFinite(totalRepoCount)
                  ? `${totalRepoCount} repositor${
                      totalRepoCount === 1 ? 'y' : 'ies'
                    }`
                  : '',
                totalRepoCount > 0 && Number.isFinite(activeRepoCount)
                  ? `${activeRepoCount} active`
                  : '',
                passiveRepoCount > 0 ? `${passiveRepoCount} passive` : '',
                connection.host || connection.webUrl,
                connection.username,
                connection.installationCount
                  ? `${connection.installationCount} installation${
                      connection.installationCount === 1 ? '' : 's'
                    }`
                  : ''
              ].filter(Boolean);

              return (
                <li key={connectionId} className="overflow-hidden">
                  <div className="flex w-full flex-col gap-3 px-4 py-3 transition-colors duration-200 hover:bg-slate-50/80 dark:hover:bg-slate-900/20 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      onClick={() => toggleConnectionExpansion(connectionId)}
                      className="flex min-w-0 flex-1 flex-col gap-3 text-left sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          title={connection.label}
                          aria-label={connection.label}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm dark:border-strokedark dark:bg-slate-900/40"
                        >
                          <ProviderIcon
                            sx={{ fontSize: 24, color: meta.color }}
                          />
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-black dark:text-white">
                              {connection.label}
                            </span>
                            <span
                              className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                                meta.mode === 'Self Hosted'
                                  ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
                                  : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200'
                              }`}
                            >
                              {meta.mode}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:justify-end">
                        {detailParts.length > 0 && (
                          <span className="text-sm text-body-color dark:text-body-color-dark">
                            {detailParts.join(' · ')}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 dark:border-strokedark dark:text-slate-200">
                          {isExpanded
                            ? 'Hide repositories'
                            : 'Show repositories'}
                          {isExpanded ? (
                            <ExpandLess sx={{ fontSize: 18 }} />
                          ) : (
                            <ExpandMore sx={{ fontSize: 18 }} />
                          )}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRefreshAuthorization(connection)}
                      disabled={
                        !connection.id || Boolean(reauthorizingConnectionId)
                      }
                      className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary/50 dark:bg-primary/10 dark:hover:bg-primary/20"
                    >
                      <RefreshRounded sx={{ fontSize: 17 }} />
                      {isReauthorizing
                        ? 'Opening authorization…'
                        : 'Refresh authorization'}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-stroke bg-slate-50/60 px-4 py-4 dark:border-strokedark dark:bg-slate-900/10">
                      {repositories.length > 0 ? (
                        <div className="max-h-80 overflow-y-auto pr-1">
                          <ul className="space-y-2">
                            {repositories.map(repository => (
                              <li
                                key={
                                  repository.id ||
                                  repository.fullName ||
                                  repository.name
                                }
                                className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-strokedark dark:bg-boxdark"
                              >
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="min-w-0">
                                    <p
                                      className="truncate text-sm font-semibold text-black dark:text-white"
                                      title={
                                        repository.fullName || repository.name
                                      }
                                    >
                                      {repository.name || repository.fullName}
                                    </p>
                                  </div>
                                  <span
                                    className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-semibold ${getRepositoryStatusClasses(
                                      repository
                                    )}`}
                                  >
                                    {repository.statusLabel || 'Passive'}
                                  </span>
                                </div>
                                {repository.status !== 'active' &&
                                  repository.reasonLabel && (
                                    <p className="text-xs text-body-color dark:text-body-color-dark">
                                      Reason: {repository.reasonLabel}
                                    </p>
                                  )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-sm text-body-color dark:text-body-color-dark">
                          No repositories have been linked to this integration
                          yet.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mb-7 flex justify-center">
        <div
          className="grid w-full max-w-md grid-cols-2 gap-1.5 rounded-2xl border border-slate-200 bg-slate-100 p-1.5 shadow-sm dark:border-slate-700 dark:bg-slate-800/80"
          role="group"
          aria-label="Integration hosting type"
        >
          {[
            { key: 'cloud', label: 'Cloud', icon: CloudRounded },
            { key: 'self-hosted', label: 'Self Hosted', icon: DnsRounded }
          ].map(option => {
            const isActive = integrationMode === option.key;
            const OptionIcon = option.icon;

            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setIntegrationMode(option.key)}
                aria-pressed={isActive}
                className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border px-5 py-3 text-sm font-bold transition-all duration-200 ${
                  isActive
                    ? 'border-primary bg-primary text-white shadow-md shadow-primary/20'
                    : 'border-transparent bg-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700/70 dark:hover:text-white'
                }`}
              >
                <OptionIcon sx={{ fontSize: 21 }} />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
        {displayedIntegrations.map(integration => (
          <IntegrationCard
            key={integration.title}
            {...integration}
            buttonText={
              integration.connectPath ? 'Connect' : integration.buttonText
            }
            onGuide={() => openSetupGuide(integration)}
            onConnect={() =>
              integration.connectPath
                ? handleStartSelfHostedConnection(integration)
                : handleStartConnection(integration)
            }
            // isDisabled={hasSelectedLeadType}
          />
        ))}
      </div>

      {connectedLeadConnections.length > 0 &&
        hasVerifiedBillingProfile &&
        !hasPaidSubscription && (
          <div className="order-last sticky bottom-4 z-30 mx-auto mt-8 w-full max-w-[1000px]">
            <div className="flex flex-col justify-between gap-4 rounded-[1.5rem] border border-cyan-300 bg-slate-950 p-4 text-white shadow-2xl shadow-cyan-950/30 sm:flex-row sm:items-center sm:px-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
                  Next step
                </p>
                <p className="mt-1 max-w-2xl text-sm font-semibold leading-6 text-slate-100">
                  Connected all your Git providers? Continue to Plan
                  Configuration to choose the right capacity for your team, or
                  connect any remaining integrations first.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/settings/plan-configuration')}
                className="shrink-0 rounded-xl bg-cyan-400 px-5 py-3 text-sm font-black text-slate-950 transition-colors hover:bg-cyan-300"
              >
                Proceed to Plan Configuration
              </button>
            </div>
          </div>
        )}

      {pendingIntegration &&
        !isConnecting &&
        createPortal(
          <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={closeConsentFlow}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="integration-consent-title"
              className="relative z-10 flex max-h-[calc(100dvh-2rem)] w-full max-w-3xl min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-stroke/80 bg-slate-100/70 px-6 py-4 dark:border-strokedark dark:bg-slate-900/40">
                <h3
                  id="integration-consent-title"
                  className="text-lg font-bold leading-7 text-black dark:text-white"
                >
                  Repository Access, Data Security and Confidentiality Consent
                </h3>
                <button
                  type="button"
                  onClick={closeConsentFlow}
                  className="rounded-md px-2 py-1 text-sm text-body-color transition-colors hover:bg-black/5 hover:text-black dark:hover:bg-white/10 dark:hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div
                ref={consentContentRef}
                onScroll={handleConsentScroll}
                role="region"
                aria-label="Consent terms"
                tabIndex={0}
                className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-white px-6 py-6 text-sm text-slate-700 dark:bg-boxdark dark:text-slate-300"
              >
                <div className="space-y-2 border-b border-stroke/80 pb-4 dark:border-strokedark">
                  <p className="text-sm font-semibold leading-6 text-black dark:text-white">
                    {CONSENT_INTRO.company}
                  </p>
                  <p className="text-sm font-medium leading-6 text-slate-700 dark:text-slate-300">
                    {CONSENT_INTRO.registration}
                  </p>
                  <p className="text-sm leading-7 text-slate-700 dark:text-slate-300">
                    {CONSENT_INTRO.description}
                  </p>
                </div>

                {CONSENT_SECTIONS.map(section => (
                  <section key={section.title} className="space-y-1.5">
                    <h4 className="text-sm font-bold leading-6 text-black dark:text-white">
                      {section.title}
                    </h4>
                    <p className="text-sm leading-7 text-slate-700 dark:text-slate-300">
                      {section.body}
                    </p>
                  </section>
                ))}
              </div>

              <div className="shrink-0 border-t border-stroke/80 px-6 py-4 dark:border-strokedark">
                <label
                  className={`mb-2 flex items-start gap-3 text-sm text-black dark:text-white ${
                    hasScrolledToConsentEnd
                      ? 'cursor-pointer'
                      : 'cursor-not-allowed opacity-70'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={hasAcceptedConsent}
                    disabled={!hasScrolledToConsentEnd}
                    onChange={event =>
                      setHasAcceptedConsent(event.target.checked)
                    }
                  />
                  <span>
                    I have read and understood Gitme’s Repository Access, Data
                    Security and Confidentiality terms.
                  </span>
                </label>
                {!hasScrolledToConsentEnd && (
                  <p className="mb-4 text-xs text-amber-700 dark:text-amber-300">
                    Please scroll to the end of the consent text before checking
                    this box.
                  </p>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handlePrimaryConsentConfirm}
                    disabled={!hasAcceptedConsent}
                    className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {pendingIntegration &&
        isConnecting &&
        createPortal(
          <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md" />
            <div className="relative z-10 max-h-[calc(100dvh-2rem)] w-full max-w-xl overflow-y-auto rounded-[28px] border border-sky-100/80 bg-white shadow-[0_32px_80px_rgba(15,23,42,0.28)]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.18),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.14),transparent_30%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]" />
              <div className="relative px-6 py-8 sm:px-8 sm:py-10">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                  Secure Handshake
                </div>

                <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-[30px] border border-sky-100 bg-white shadow-[0_20px_50px_rgba(56,189,248,0.18)]">
                  <div
                    className="flex h-16 w-16 animate-[spin_8s_linear_infinite] items-center justify-center rounded-full border-4 border-dashed"
                    style={{ borderColor: `${pendingIntegration.color}35` }}
                  >
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm"
                      style={{
                        backgroundColor: `${pendingIntegration.color}18`
                      }}
                    >
                      <pendingIntegration.icon
                        sx={{ fontSize: 28, color: pendingIntegration.color }}
                      />
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-3xl font-black tracking-tight text-slate-950">
                    Securing connection
                  </h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-600">
                    We&apos;re preparing a secure redirect to{' '}
                    {pendingIntegration.title}.
                  </p>
                </div>

                <div className="mt-8 rounded-2xl border border-slate-200/80 bg-slate-50/90 p-4">
                  <div className="h-3 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${connectionProgress}%`,
                        background: `linear-gradient(90deg, ${pendingIntegration.color} 0%, #38bdf8 65%, #22c55e 100%)`
                      }}
                    />
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3 text-sm text-slate-600">
                    <span>{connectionStatus}</span>
                    <span className="font-semibold text-slate-900">
                      {connectionProgress}%
                    </span>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3 text-center text-xs text-slate-500">
                  <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3">
                    Limited access
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3">
                    No secret capture
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3">
                    Redirecting shortly
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
export default Integrations;
